import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import React, { FC, useEffect, useState } from 'react'
import { Helmet } from 'react-helmet'
import { useDispatch, useSelector } from 'react-redux'
import { useHistory, useLocation, useParams } from 'react-router-dom'
import { RootState } from '../../../../states/containers/store'
import {
  createTaskLabelGroupCategory,
  destroyTaskLabelGroupCategory,
  fetchTaskLabelGroupCategory,
  updateTaskLabelGroupCategory,
} from '../../../../states/containers/task/taskSlice'
import { RequestPatchTaskLabelGroupCategory, RequestPostTaskLabelGroupCategory } from '../../../../states/types/task'
import LoadingSquare from '../../../components/common/LoadingSquare'
import Tips from '../../../components/common/Tips'
import ConfirmationModal from '../../../components/task/ConfirmationModal'
import { useAlert } from 'react-alert'
import AddLabelGroupCategoryModal from '../../../components/task/AddLabelGroupCategoryModal'
import { URL } from '../../../constants/url'
import { setPath, setUrlId } from '../../../../utils/url'
import { LangValue } from '../../../../states/types/common'
import { uniqueId } from 'lodash'
import ViewLabelGroupModal from '../../../components/task/ViewLabelGroupModal'

interface MemberDetail {
  member_id: number
  name: string
  kana: string | null
  profile_image: string | null
}

interface LabelGroup {
  id: number
  name: string
  code: string | null
  color: string
}

interface LabelGroupCategories {
  id: number
  category_name: string
  color: string
  parent_id: number | null
  members: MemberDetail[]
  label_groups: LabelGroup[]
  label_group_count: number
}

interface CustomLabelGroupCategories extends LabelGroupCategories {
  parent_members: MemberDetail[]
}

interface ModalState {
  id?: number
  name?: string
  color?: string
  parent_id?: number | null
  member_ids?: number[]
  label_group_ids?: number[]
  type: string
}

interface ConfirmationModalState {
  display: boolean
  type: string
  name: string
  callback: VoidFunction
}

interface State {
  create: boolean;
}

const Index: FC = () => {
  const {
    task,
    login,
  } = useSelector((state: RootState) => state)

  const { language } = useParams<{ language: LangValue }>()
  const location = useLocation()
  const dispatch = useDispatch()
  const alert = useAlert()
  const history = useHistory()

  const [data, setData] = useState<LabelGroupCategories[]>([])
  const [members, setMembers] = useState<MemberDetail[]>([])
  const [labelGroups, setLabelGroups] = useState<LabelGroup[]>([])

  const [isDisplayAddTask, setIsDisplayAddTask] = useState<boolean>(false)

  const [openCategoryIds, setOpenCategoryIds] = useState<number[]>([])

  const [confirmationModalState, setConfirmationModalState] = useState<ConfirmationModalState>({
    display: false,
    type: "",
    name: "",
    callback: () => {
      // none
    },
  })

  const modalStateInit = {
    type: "create",
  }

  const [modalState, setModalState] = useState<ModalState>(modalStateInit)

  const [pollingTrigger, setPollingTrigger] = useState<string>("")

  const [isDisplayViewLabelGroupModal, setIsDisplayViewLabelGroupModal] = useState<boolean>(false)
  const [viewLabelGroup, setViewLabelGroup] = useState<{id: number, category_name: string, color: string}>()

  // eslint-disable-next-line max-len
  const addProjectCategory = (param: {category_name: string, color: string, parent_id?: number | null, member_ids?: number[], label_group_ids?: number[] }) => {
    const tmpValue: RequestPostTaskLabelGroupCategory = {
      category_name: param.category_name,
      color: param.color,
      parent_id: param.parent_id || null,
      member_ids: param.member_ids || [],
      label_group_ids: param.label_group_ids || [],
      scheduled_start_date: null,
      scheduled_end_date: null,
    }
    dispatch(
      createTaskLabelGroupCategory(
        tmpValue,
        () => {
          alert.success(`カテゴリー「${param.category_name}」を追加しました`)
        },
      ),
    )
    setIsDisplayAddTask(false)
  }

  // eslint-disable-next-line max-len
  const modProjectCategory = (param: { id: number, category_name?: string, color?: string, parent_id?: number | null, member_ids?: number[], add_label_group_ids?: number[], remove_label_group_ids?: number[] }) => {
    const labelGroupCategory = task.taskLabelGroupCategory?.label_group_categories.find((v) => v.id === param.id)
    const tmpValue: RequestPatchTaskLabelGroupCategory = { id: param.id.toString() }
    if (labelGroupCategory?.category_name !== param.category_name) tmpValue.category_name = param.category_name
    if (labelGroupCategory?.color !== param.color) tmpValue.color = param.color
    if (labelGroupCategory?.parent_id !== param.parent_id) {
      if (param.parent_id) {
        tmpValue.parent_id = param.parent_id
      } else {
        tmpValue.parent_id = null
      }
    } else {
      tmpValue.parent_id = labelGroupCategory?.parent_id || null
    }
    tmpValue.member_ids = param.member_ids || []
    tmpValue.label_groups = {
      added_ids: param.add_label_group_ids,
      removed_ids: param.remove_label_group_ids,
    }
    dispatch(updateTaskLabelGroupCategory(
      tmpValue,
      () => {
        alert.success(`カテゴリー「${param.category_name}」を変更しました`)
      },
    ))
    setIsDisplayAddTask(false)
  }

  const openLabelGroupCategoryModal = (
    type: string, id?: number, name?: string, color?: string, parent_id?: number | null, member_ids?: number[], label_group_ids?: number[],
  ) => {
    if (type === "create") {
      setModalState(
        {
          type: "create",
        },
      )
    } else {
      setModalState(
        {
          type: "update",
          id: id,
          name: name,
          parent_id: parent_id,
          member_ids: member_ids,
          label_group_ids: label_group_ids,
          color: color,
        },
      )
    }
    setIsDisplayAddTask(true)
  }

  // confirmation modal
  const openConfirmationModal = (type: string, id: number) => {
    let callback = () => {
      // none
    }
    const labelGroupCategory = task.taskLabelGroupCategory?.label_group_categories.find((v) => v.id === id)
    switch (type) {
      case "category_delete":
        callback = () => {
          dispatch(
            destroyTaskLabelGroupCategory(
              { id: id.toString() },
              () => {
                alert.success(`カテゴリー「${labelGroupCategory?.category_name}」を削除しました`)
              },
            ),
          )
        }
        break
      default:
        break
    }
    const name = data.find((v) => v.id === id)?.category_name
    setConfirmationModalState({
      ...confirmationModalState,
      display: true,
      type: type,
      name: name || "",
      callback: callback,
    })
  }

  const closeConfirmationModal = () => {
    setConfirmationModalState({
      ...confirmationModalState,
      display: false,
      type: "",
      name: "",
      callback: () => {
        // none
      },
    })
  }

  const searchMemberIds = (id: number | null): number[] => {
    if (!id) return []
    const target = data.find((v) => v.id === id)
    return Array.from(new Set([
      ...target?.members.map((v) => v.member_id) || [],
      ...searchMemberIds(data.find((v) => v.id === target?.parent_id)?.id || null).flat(),
    ]))
  }

  const viewReport = (id: number) => {
    history.push(`${setPath(setUrlId(URL.TASK_CATEGORY_DETAIL_DETAIL.path, 'id', id), language)}`)
  }

  const toggleCategory = (categoryId: number) => {
    if (openCategoryIds.includes(categoryId)) {
      setOpenCategoryIds(openCategoryIds.filter((v) => v !== categoryId))
    } else {
      setOpenCategoryIds([...openCategoryIds, categoryId])
    }
  }

  const buildCategoryList = (parent_id?: number, parent_members?: MemberDetail[]): CustomLabelGroupCategories[] => {
    let list = []
    if (parent_id) {
      list = data.filter((v) => v.parent_id === parent_id)
    } else {
      list = data.filter((v) => v.parent_id === null)
    }
    if (!list.length) return []
    const current = list.map((v) => ({ ...v, parent_members: parent_members || [] }))
    return Array.from(
      new Set(
        [
          ...current,
          ...list.map((v) => buildCategoryList(
            v.id,
            Array.from(new Map([...parent_members || [], ...v.members].map((w) => [w.member_id, w])).values()),
          )).flat(),
        ],
      ),
    )
  }

  const clickViewProjectModal = (labelGroupCategoryId: number) => {
    const target = task.taskLabelGroupCategory?.label_group_categories.find((v) => v.id === labelGroupCategoryId)
    if (!target) return
    setViewLabelGroup({ id: target.id, category_name: target.category_name, color: target.color })
    setIsDisplayViewLabelGroupModal(true)
  }

  const displayCategoryList = (level = 0, parent_id?: number, rules?: boolean[], parent_members?: MemberDetail[]):JSX.Element => {
    let list = []
    const levelCount = level + 1
    if (parent_id) {
      list = data.filter((v) => v.parent_id === parent_id)
    } else {
      list = data.filter((v) => v.parent_id === null)
    }
    if (!list.length) return (<></>)
    return (
      <>
        {
          list.map((v, i) => {
            const ruleList = [...rules?.map((rule, index) => (rules.length === index + 1 ? !rule : rule)) || [], list.length === i + 1]
            return (
              <React.Fragment key={`category_name_${v.category_name}`}>
                <div className="row">
                  <div>
                    {
                      searchMemberIds(v.id).includes(login.current_member_id || 0) && (
                        <Tips
                          description="レポートを見る"
                          className="view"
                          icon={false}
                        >
                          <>
                            <FontAwesomeIcon
                              icon={['fas', 'file-alt']}
                              style={{ cursor: "pointer" }}
                              onClick={() => viewReport(v.id)}
                            />
                          </>
                        </Tips>
                      )
                    }
                    <Tips
                      description="編集する"
                      className="edit"
                      icon={false}
                    >
                      <>
                        <FontAwesomeIcon
                          icon={['fas', 'tools']}
                          style={{ cursor: "pointer" }}
                          onClick={() => openLabelGroupCategoryModal(
                            "update", v.id, v.category_name, v.color, v.parent_id, v.members.map((w) => w.member_id), v.label_groups.map((w) => w.id),
                          )}
                        />
                      </>
                    </Tips>
                    <Tips
                      description="削除する"
                      className="delete"
                      icon={false}
                    >
                      <>
                        <FontAwesomeIcon
                          icon={['fas', 'minus-circle']}
                          style={{ cursor: "pointer" }}
                          onClick={() => openConfirmationModal("category_delete", v.id)}
                        />
                      </>
                    </Tips>
                  </div>
                  <div
                    style={{
                      flexFlow: "column",
                      justifyContent: "center",
                      alignItems: "flex-start",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: "8px",
                        width: "100%",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "8px",
                        }}
                      >
                        {
                          ruleList.map((w, j) => {
                            if (!j) return (<React.Fragment key={`${v.category_name}_${j + 0}_${level}`} />)
                            return (
                              <div
                                key={`${v.category_name}_${j + 0}_${level}`}
                                style={{
                                  width: "14px",
                                }}
                              >
                                {
                                  ruleList.length === j + 1 ? (
                                    <>
                                      {w ? (<>┗</>) : (<>┣</>)}
                                    </>
                                  ) : (
                                    <>
                                      {w && (<>┃</>)}
                                    </>
                                  )
                                }
                              </div>
                            )
                          })
                        }
                        <div className="row_name_area">
                          <FontAwesomeIcon
                            icon={['fas', 'folder']}
                            className="icon"
                            style={{
                              color: v.color,
                              fontSize: "20px",
                            }}
                          />
                          {v.category_name}
                        </div>
                      </div>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "flex-end",
                          gap: "4px",
                          minWidth: "138px",
                        }}
                      >
                        <div
                          style={{
                            display: "flex",
                            alignItems: "baseline",
                            fontSize: "8px",
                            color: "#999999",
                            gap: "2px",
                          }}
                        >
                          <div
                            style={{
                              fontSize: "14px",
                            }}
                          >
                            {v.label_group_count}
                          </div>
                          件のプロジェクト
                        </div>
                        {
                          login.permissions?.high_volume && (
                            <button
                              type="button"
                              style={{
                                border: "solid 1px #999999",
                                borderRadius: "4px",
                                backgroundColor: "#EEEEEE",
                                color: "#666666",
                                fontSize: "10px",
                                padding: "4px 8px",
                              }}
                              onClick={() => clickViewProjectModal(v.id)}
                            >
                              確認する
                            </button>
                          )
                        }
                      </div>
                    </div>
                    {
                      !login.permissions?.high_volume && !!v.label_groups.length && (
                        <div
                          style={{
                            width: `calc(100% - ${22 * (ruleList.length - 1)}px)`,
                            marginLeft: `${22 * (ruleList.length - 1)}px`,
                            border: "solid 1px #CCCCCC",
                            borderRadius: "4px",
                            fontWeight: "normal",
                            fontSize: "12px",
                            color: "#666666",
                            backgroundColor: "#f5fbff",
                          }}
                        >
                          <div
                            style={{
                              padding: "4px",
                            }}
                          >
                            プロジェクト
                          </div>
                          <div
                            style={{
                              display: "flex",
                              alignItems: "flex-start",
                              flexWrap: "wrap",
                              gap: "4px",
                              padding: "4px",
                            }}
                          >
                            {
                              v.label_groups.map((w) => (
                                <div
                                  style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "4px",
                                    border: "solid 1px #CCCCCC",
                                    borderRadius: "20px",
                                    padding: "4px 8px",
                                    fontSize: "10px",
                                    backgroundColor: "#FFFFFF",
                                  }}
                                >
                                  <div
                                    style={{
                                      backgroundColor: w.color,
                                      width: "10px",
                                      height: "10px",
                                    }}
                                  />
                                  {w.name}
                                  {w.code && `(${w.code})`}
                                </div>
                              ))
                            }
                          </div>
                        </div>
                      )
                    }
                  </div>
                  <div>
                    {
                      levelCount > 1 && (
                        <div
                          style={{
                            border: "solid 1px #4372ba",
                            padding: "8px",
                            backgroundColor: "#e2edff",
                            width: "100%",
                          }}
                        >
                          <div
                            style={{
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center",
                            }}
                          >
                            親階層のカテゴリー表示メンバー
                            <button
                              type="button"
                              onClick={() => toggleCategory(v.id)}
                              style={{
                                padding: "2px 4px",
                                border: "solid 1px #666666",
                                borderRadius: "4px",
                                fontSize: "9px",
                                backgroundColor: "#FFFFFF",
                              }}
                            >
                              {openCategoryIds.includes(v.id) ? "閉じる" : "開く"}
                            </button>
                          </div>
                          {
                            openCategoryIds.includes(v.id) && (
                              <div
                                style={{
                                  display: "flex",
                                  flexWrap: "wrap",
                                  gap: "4px",
                                  marginTop: "8px",
                                }}
                              >
                                {
                                  parent_members?.map((member) => (
                                    <div
                                      className="member_block"
                                      key={`${v.category_name}_member_${member.member_id}`}
                                    >
                                      {
                                        member.profile_image ? (
                                          <img
                                            alt="profile"
                                            src={member.profile_image}
                                            className="profile_icon"
                                          />
                                        ) : (
                                          <img
                                            alt=""
                                            src={`/static/images/profile/${(Number(member.member_id) || 0) % 12}.jpeg`}
                                            className="profile_icon"
                                          />
                                        )
                                      }
                                      {member.name}
                                    </div>
                                  ))
                                }
                              </div>
                            )
                          }
                        </div>
                      )
                    }
                    {
                      v.members.map((member) => (
                        <div
                          className="member_block"
                          key={`${v.category_name}_member_${member.member_id}`}
                        >
                          {
                            member.profile_image ? (
                              <img
                                alt="profile"
                                src={member.profile_image}
                                className="profile_icon"
                              />
                            ) : (
                              <img
                                alt=""
                                src={`/static/images/profile/${(Number(member.member_id) || 0) % 12}.jpeg`}
                                className="profile_icon"
                              />
                            )
                          }
                          {member.name}
                        </div>
                      ))
                    }
                  </div>
                </div>
                {
                  displayCategoryList(
                    levelCount,
                    v.id,
                    ruleList,
                    levelCount > 1 ? Array.from(new Map([...parent_members || [], ...v.members].map((w) => [w.member_id, w])).values()) : v.members,
                  )
                }
              </React.Fragment>
            )
          })
        }
      </>
    )
  }

  useEffect(() => {
    window.scrollTo(0, 0)
    const tmpState = location.state as State
    if (tmpState?.create) {
      openLabelGroupCategoryModal("create")
    }
  }, [location])

  useEffect(() => {
    dispatch(fetchTaskLabelGroupCategory())
  }, [])

  useEffect(() => {
    setData(task.taskLabelGroupCategory?.label_group_categories || [])
    setMembers(task.taskLabelGroupCategory?.members || [])
    setLabelGroups(task.taskLabelGroupCategory?.label_groups || [])
  }, [task.taskLabelGroupCategory])

  useEffect(() => {
    if (!pollingTrigger) return
    const polling = setTimeout(() => {
      dispatch(fetchTaskLabelGroupCategory())
    }, 10 * 1000)
    // eslint-disable-next-line consistent-return
    return () => {
      if (pollingTrigger) {
        clearTimeout(polling)
      }
    }
  }, [pollingTrigger])

  useEffect(() => {
    setPollingTrigger(!task.taskLabelGroupCategory?.importing_project ? "" : uniqueId())
  }, [task.taskLabelGroupCategory])

  return (
    <section className="task_page setting_page">
      <Helmet>
        <title>Qasee - 工数管理 - 設定 - カテゴリー設定</title>
      </Helmet>
      <section className="breadcrumb">
        工数管理
        &nbsp;&gt;&nbsp;
        設定
        &nbsp;&gt;&nbsp;
        カテゴリー設定
        &nbsp;
      </section>
      <section className="page_header">
        <h1>
          カテゴリー設定
        </h1>
        <div className="tools">
          {
            !pollingTrigger && (
              <div className="button_area">
                <button
                  type="button"
                  className="button"
                  onClick={() => openLabelGroupCategoryModal("create")}
                >
                  <FontAwesomeIcon icon={['fas', 'plus']} />
                  &nbsp;
                  カテゴリーを新規作成する
                </button>
              </div>
            )
          }
        </div>
      </section>
      <section className="page_description">
        <p>
          プロジェクトをまとめて管理するためのカテゴリーの新規作成、既存カテゴリーの編集、削除をすることができます。
        </p>
        <p>
          カテゴリー表示メンバーは、カテゴリーレポートの参照、所属しているプロジェクトの仕分け状況確認、プロジェクトレポート参照などができます。
        </p>
      </section>
      {
        pollingTrigger ? (
          <>
            <LoadingSquare>
              <>
                プロジェクトインポート処理が実行中です。しばらくおまちください。
              </>
            </LoadingSquare>
          </>
        ) : (
          <>
            <section className="page_contents content-area">
              <div className="content-block">
                <div className="block-detail">
                  {
                    task.isLoading ? (
                      <LoadingSquare>
                        <>
                          カテゴリーリストを読み込んでいます…
                        </>
                      </LoadingSquare>
                    ) : (
                      <>
                        {
                          data.length ? (
                            <>
                              <div className="project_category_table">
                                <div className="row_head">
                                  <div>
                                    編集
                                  </div>
                                  <div>
                                    カテゴリー名
                                  </div>
                                  <div>
                                    カテゴリー表示メンバー
                                  </div>
                                </div>
                                {displayCategoryList()}
                              </div>
                            </>
                          ) : (
                            <>
                              <div className="no_data_area">
                                <div className="no_data">
                                  対象のカテゴリーはありません
                                </div>
                              </div>
                            </>
                          )
                        }
                      </>
                    )
                  }
                </div>
              </div>
            </section>
          </>
        )
      }
      <AddLabelGroupCategoryModal
        modalState={modalState}
        closeCallback={() => setIsDisplayAddTask(false)}
        addCallback={(param) => addProjectCategory(param)}
        modCallback={(param) => modProjectCategory(param)}
        isDisplay={isDisplayAddTask}
        categoryList={buildCategoryList()}
        memberList={members.map((v) => ({ member_id: v.member_id, name: v.name, profile_image: v.profile_image, kana: v.kana }))}
        labelGroupList={labelGroups}
      />
      <ViewLabelGroupModal
        isDisplay={isDisplayViewLabelGroupModal}
        labelGroupCategory={viewLabelGroup}
        closeCallback={() => setIsDisplayViewLabelGroupModal(false)}
      />
      {/* 確認モーダル */}
      <ConfirmationModal
        confirmationModalState={confirmationModalState}
        closeCallback={closeConfirmationModal}
      />
    </section>
  )
}

export default Index
